import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconBell } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  useBillingControllerCompanyQuery,
  useStripeAutoTopUpControllerReadQuery,
  useUserMeControllerUserMeQuery,
  type CompanyBillingSummaryDto,
} from '@shared/services/apiService';
import { useAppSelector } from '@shared/store';
import { billingAlerts } from '../model/billingAlerts';

export const BillingAlerts = ({
  companyId,
  summary,
}: {
  companyId: string;
  summary: CompanyBillingSummaryDto;
}) => {
  const auto = useStripeAutoTopUpControllerReadQuery(
    { companyId },
    { refetchOnMountOrArgChange: true },
  );
  const alerts = billingAlerts(summary, auto.isError ? undefined : auto.currentData);
  return (
    <>
      {alerts.map((a) => (
        <Alert key={a.key} severity={a.severity}>
          <strong>{a.title}.</strong> {a.detail}
        </Alert>
      ))}
    </>
  );
};
export const BillingNotifications = () => {
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const me = useUserMeControllerUserMeQuery();
  const companyId = user?.companyId;
  if (!companyId || me.isError || me.currentData?.companyId !== companyId) return null;
  return (
    <CompanyBillingNotifications
      key={`${companyId}:${user.id}:${user.role}`}
      companyId={companyId}
    />
  );
};
const CompanyBillingNotifications = ({ companyId }: { companyId: string }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const options = {
    refetchOnMountOrArgChange: true,
    pollingInterval: 60000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
  };
  const summary = useBillingControllerCompanyQuery({ companyId }, options);
  const auto = useStripeAutoTopUpControllerReadQuery(
    summary.currentData?.mode === 'credit_managed' ? { companyId } : skipToken,
    options,
  );
  const facts = summary.isError ? undefined : summary.currentData;
  const alerts = facts ? billingAlerts(facts, auto.isError ? undefined : auto.currentData) : [];
  if (facts?.mode !== 'credit_managed') return null;
  return (
    <>
      <Tooltip title="Billing notifications">
        <IconButton
          aria-label={`Billing notifications: ${alerts.length} active`}
          onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge badgeContent={alerts.length} color="warning">
            <IconBell size={20} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Stack spacing={2} sx={{ p: 2.5, width: 360, maxWidth: 'calc(100vw - 32px)' }}>
          <Typography variant="h6">Billing notifications</Typography>
          {alerts.length ? (
            alerts.map((a) => (
              <Box key={a.key}>
                <Typography variant="body2" fontWeight={600}>
                  {a.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {a.detail}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No billing actions are needed right now.
            </Typography>
          )}
          <Button component={Link} to="/billing" onClick={() => setAnchor(null)}>
            Open Billing
          </Button>
        </Stack>
      </Popover>
    </>
  );
};
